import { getCachedClubShield, DEFAULT_CLUB_SHIELD } from '../../../core/state/club-shield-cache';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClubShieldConfig } from '../../../core/models/types';
import { ShieldTemplateRegistry } from '../../../core/services/shield-template-registry.service';

export type ShieldSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'custom';

@Component({
  selector: 'app-club-shield',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="club-shield-wrapper inline-flex items-center justify-center shrink-0 select-none overflow-hidden"
      [class]="containerClasses()"
    >
      @if (renderedSvg(); as svg) {
        <img class="w-full h-full object-contain" [src]="svg" [alt]="altText()" />
      } @else if (effectiveFallbackCrest(); as crestUrl) {
        <img
          [src]="crestUrl"
          [alt]="altText()"
          class="w-full h-full object-contain"
          referrerpolicy="no-referrer"
          (error)="onImageError()"
        />
      } @else {
        <!-- Fallback badge when neither custom shield nor crest image is present -->
        <div
          class="w-full h-full rounded flex items-center justify-center font-bold text-white shadow-2xs"
          [style.backgroundColor]="fallbackColor()"
        >
          <span>{{ initialsText() }}</span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        vertical-align: middle;
      }
      .club-shield-wrapper {
        aspect-ratio: 200 / 240;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClubShieldComponent {
  private readonly registry = inject(ShieldTemplateRegistry);

  readonly originalTemplate = input<boolean>(false);

  // Direct Shield configuration
  readonly shield = input<ClubShieldConfig | null | undefined>(undefined);

  // Individual parameters
  readonly templateId = input<string | null | undefined>(undefined);
  readonly primaryColor = input<string | null | undefined>(undefined);
  readonly secondaryColor = input<string | null | undefined>(undefined);
  readonly textColor = input<string | null | undefined>(undefined);
  readonly initials = input<string | null | undefined>(undefined);

  // Optional whole club object or identity parameters
  readonly club = input<any>(null);
  readonly clubId = input<string | null | undefined>(undefined);
  readonly clubName = input<string | null | undefined>(undefined);
  readonly clubCode = input<string | null | undefined>(undefined);

  // Sizing and styling
  readonly size = input<ShieldSize>('md');
  readonly customClass = input<string>('');

  // Fallbacks
  readonly fallbackCrestUrl = input<string | null | undefined>(undefined);
  readonly fallbackText = input<string>('');
  readonly fallbackBgColor = input<string>('');

  readonly imageError = signal(false);

  /**
   * Determine the effective shield config
   */
  readonly effectiveShield = computed<ClubShieldConfig | null>(() => {
    // Club-bound views follow the latest server cache; editor previews have no club ID.
    const id = this.clubId() || this.club()?.id;
    const cached = id ? getCachedClubShield(id) : null;
    if (cached) return cached;

    // 1. Direct shield input
    const directShield = this.shield();
    if (directShield && directShield.templateId) {
      return directShield;
    }

    // 2. Individual inputs provided
    const tid = this.templateId();
    if (tid) {
      return {
        templateId: tid,
        primaryColor: this.primaryColor() || '#123D83',
        secondaryColor: this.secondaryColor() || '#F5B800',
        textColor: this.textColor() || this.primaryColor() || '#123D83',
        initials: (this.initials() ?? 'LOM').slice(0, 3).toUpperCase(),
      };
    }

    const c = this.club();
    if (c?.shield?.templateId) return c.shield;
    if (id) return DEFAULT_CLUB_SHIELD;

    return null;
  });

  /**
   * Safe SVG output from template registry
   */
  readonly renderedSvg = computed<string | null>(() => {
    const s = this.effectiveShield();
    if (!s) return null;
    const rawSvg = this.originalTemplate()
      ? this.registry.getTemplate(s.templateId)?.svgTemplate
      : this.registry.renderShieldSvg(
      s.templateId,
      s.primaryColor,
      s.secondaryColor,
      s.initials,
      s.textColor
    );
    // An image gives each SVG its own document: gradient/filter/clip IDs cannot collide.
    return rawSvg ? 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(rawSvg) : null;
  });

  /**
   * Fallback crest image URL when no custom shield is active
   */
  readonly effectiveFallbackCrest = computed<string | null>(() => {
    if (this.imageError()) return null;
    if (this.fallbackCrestUrl()) return this.fallbackCrestUrl()!;

    const c = this.club();
    if (!c) return null;

    const code = c.code || c.shortName || '';
    if (code) {
      return `/crest/${code.toUpperCase()}.png`;
    }
    return null;
  });

  readonly fallbackColor = computed<string>(() => {
    if (this.fallbackBgColor()) return this.fallbackBgColor();
    const c = this.club();
    return c?.primaryColor || c?.color || '#1e293b';
  });

  readonly initialsText = computed<string>(() => {
    if (this.initials()) return this.initials()!.slice(0, 3).toUpperCase();
    if (this.fallbackText()) return this.fallbackText().slice(0, 3).toUpperCase();
    const c = this.club();
    if (c?.code) return c.code.slice(0, 3).toUpperCase();
    if (c?.name) return c.name.slice(0, 3).toUpperCase();
    return 'LOM';
  });

  readonly altText = computed<string>(() => {
    const c = this.club();
    return c?.name || this.initialsText() || 'Club Shield';
  });

  readonly containerClasses = computed<string>(() => {
    const custom = this.customClass();
    let sizeClasses = '';
    switch (this.size()) {
      case 'xs':
        sizeClasses = 'w-4 h-5 text-[8px]';
        break;
      case 'sm':
        sizeClasses = 'w-5 h-6 text-[9px]';
        break;
      case 'md':
        sizeClasses = 'w-8 h-9.5 text-xs';
        break;
      case 'lg':
        sizeClasses = 'w-10 h-12 text-sm';
        break;
      case 'xl':
        sizeClasses = 'w-14 h-16.5 text-base';
        break;
      case '2xl':
        sizeClasses = 'w-20 h-24 text-xl';
        break;
      case 'custom':
      default:
        sizeClasses = 'w-full h-full';
        break;
    }
    return custom ? `${sizeClasses} ${custom}`.trim() : sizeClasses;
  });

  onImageError(): void {
    this.imageError.set(true);
  }
}
